const skeleton = document.querySelector('[data-page-loading-skeleton]');
const SHOW_DELAY = 140;
const NAVIGATION_RECOVERY_DELAY = 10000;
const PAGE_LOADING_EVENT = 'sigma:page-loading';
const BROWSING_MODE_STORAGE_KEY = 'sigma-coffee-browsing-mode';
const ROUTE_ENTRY_STORAGE_KEY = 'sigma-route-entry';
const LAYOUT_STORAGE_KEY = 'sigma-loading-layout-v1';
const insideThemeEditor = Boolean(window.Shopify?.designMode && window.self !== window.top);

if (skeleton && !insideThemeEditor) {
  let showTimer = 0;
  let recoveryTimer = 0;
  let captureTimer = 0;
  let captureIdle = 0;
  let pendingDestination = null;
  let pendingVariant = null;
  let pendingProductState = null;
  let layoutApi = null;
  let layoutImport = null;
  const blockedElements = new Set();
  const isAccountVariant = (variant) => variant === 'account-sign-in' || variant === 'account-sign-out';
  const collectionIsPending = () => document.documentElement.matches(
    '[data-collection-initial-loading], [data-collection-initial-url]:not([data-collection-initial-fallback])',
  ) || Boolean(document.querySelector('[data-collection-browsing][aria-busy="true"]'));

  const loadLayoutApi = () => {
    layoutImport ||= import('./loading-layout.js')
      .then((api) => (layoutApi = api))
      .catch(() => null);
    return layoutImport;
  };

  const setPageContentBlocked = (blocked) => {
    if (blocked) {
      const pageContent =
        typeof document.querySelectorAll === 'function'
          ? document.querySelectorAll('.skip-link, #MainContent, .shopify-section-group-footer-group')
          : [];

      pageContent.forEach((element) => {
        if (!(element instanceof HTMLElement) || element.inert) {
          return;
        }

        element.inert = true;
        blockedElements.add(element);
      });
      return;
    }

    blockedElements.forEach((element) => {
      element.inert = false;
    });
    blockedElements.clear();
  };

  const clearRouteEntry = () => {
    try {
      window.sessionStorage.removeItem(ROUTE_ENTRY_STORAGE_KEY);
    } catch {}
  };

  const browsingMode = () => {
    const collection = document.querySelector('[data-collection-browsing]');

    if (collection instanceof HTMLElement) {
      return collection.dataset.collectionBrowsingMode === 'advanced' ? 'advanced' : 'basic';
    }

    try {
      return window.localStorage.getItem(BROWSING_MODE_STORAGE_KEY) === 'advanced'
        ? 'advanced'
        : 'basic';
    } catch {
      return 'basic';
    }
  };

  // Cache anonymous geometry after the page is usable, never on its loading path.
  const layoutKey = (destination) => JSON.stringify([
    destination.pathname, destination.search, window.innerWidth, window.innerHeight,
    browsingMode(), Boolean(skeleton.querySelector('.loyalty-loading--member')),
    destination.pathname.endsWith('/cart')
      ? `${document.querySelector('[data-cart-drawer-content]')?.dataset.cartItemCount || '0'}:${document.querySelector('[data-cart-drawer-content]')?.dataset.cartLineCount || '0'}`
      : '',
  ]);

  const readLayouts = () => {
    try {
      const layouts = JSON.parse(window.sessionStorage.getItem(LAYOUT_STORAGE_KEY) || '[]');
      return Array.isArray(layouts) ? layouts.slice(-8) : [];
    } catch {
      return [];
    }
  };

  const rememberLayout = async () => {
    if (!skeleton.hidden || document.body.hasAttribute('aria-busy')) return;
    if (collectionIsPending()) return;
    const api = await loadLayoutApi();
    if (!api || !skeleton.hidden || document.body.hasAttribute('aria-busy') || collectionIsPending()) return;
    const main = document.querySelector('#MainContent');
    if (!main || main.querySelector('[data-for-you-loading]:not([hidden]), [data-loyalty-loading-fallback]:not([hidden]), [data-store-credit-loading]:not([hidden])')) return;
    const layout = api.captureLoadingLayout(main);
    if (!layout) return;
    const key = layoutKey(new URL(window.location.href));
    try {
      const layouts = readLayouts().filter((entry) => entry?.key !== key);
      layouts.push({ key, layout });
      window.sessionStorage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(layouts.slice(-8)));
    } catch {}
  };

  const queueLayoutCapture = () => {
    window.clearTimeout(captureTimer);
    window.cancelIdleCallback?.(captureIdle);
    captureTimer = window.setTimeout(() => {
      if (typeof window.requestIdleCallback === 'function') {
        captureIdle = window.requestIdleCallback(() => {
          captureIdle = 0;
          void rememberLayout();
        });
      } else {
        rememberLayout();
      }
    }, 250);
  };

  const setVariant = (variant) => {
    layoutApi?.clearLoadingLayout(skeleton);
    skeleton.dataset.pageLoadingVariant = variant;
    if (variant === 'product' || variant === 'product-coffee' || variant === 'product-equipment') {
      skeleton.dataset.pageLoadingProductState = pendingProductState || 'available';
    } else {
      skeleton.removeAttribute('data-page-loading-product-state');
    }

    if (variant === 'browse' || variant === 'for-you' || variant === 'for-you-signed-out') {
      skeleton.dataset.pageLoadingCardMode = browsingMode();
    } else {
      skeleton.removeAttribute('data-page-loading-card-mode');
    }

    if (variant === 'cart') {
      const cartContent = document.querySelector('[data-cart-drawer-content]');
      const itemCount = Number(cartContent?.dataset.cartItemCount || 0);
      const lineCount = Number(cartContent?.dataset.cartLineCount || cartContent?.querySelectorAll('.cart-line').length || 0);
      skeleton.dataset.pageLoadingCartState = itemCount > 0 ? 'populated' : 'empty';
      skeleton.dataset.pageLoadingCartItems = String(Math.min(3, lineCount));
    } else {
      skeleton.removeAttribute('data-page-loading-cart-state');
      skeleton.removeAttribute('data-page-loading-cart-items');
    }
  };

  const syncHeaderOffset = () => {
    const header = document.querySelector('.site-header');
    const headerBottom = header instanceof HTMLElement ? header.getBoundingClientRect().bottom : 0;

    skeleton.style.setProperty('--page-loading-header-offset', `${Math.max(0, Math.round(headerBottom))}px`);
  };

  const paintLayout = (layout) => {
    if (!layoutApi?.renderLoadingLayout(skeleton, layout)) return false;
    const headerBottom = Math.max(0, document.querySelector('.site-header')?.getBoundingClientRect().bottom || 0);
    const layer = skeleton.querySelector(':scope > .loading-layout');
    layer.style.left = `${layout.originX}px`;
    layer.style.top = `${layout.originY - headerBottom}px`;
    return true;
  };

  const hide = () => {
    window.clearTimeout(showTimer);
    window.clearTimeout(recoveryTimer);
    showTimer = 0;
    recoveryTimer = 0;
    skeleton.removeAttribute('data-visible');
    skeleton.removeAttribute('data-page-loading-variant');
    skeleton.removeAttribute('data-page-loading-card-mode');
    skeleton.removeAttribute('data-page-loading-cart-state');
    skeleton.removeAttribute('data-page-loading-cart-items');
    skeleton.removeAttribute('data-page-loading-product-state');
    skeleton.hidden = true;
    layoutApi?.clearLoadingLayout(skeleton);
    document.body.removeAttribute('aria-busy');
    setPageContentBlocked(false);
  };

  const show = () => {
    if (!skeleton.hidden) {
      return;
    }

    document.body.setAttribute('aria-busy', 'true');
    setPageContentBlocked(true);
    syncHeaderOffset();
    if (pendingDestination && !isAccountVariant(pendingVariant)) {
      const cached = readLayouts().find((entry) => entry?.key === layoutKey(pendingDestination));
      if (cached?.layout && !paintLayout(cached.layout)) {
        const destination = pendingDestination;
        void loadLayoutApi().then(() => {
          if (!skeleton.hidden && pendingDestination === destination) paintLayout(cached.layout);
        });
      }
    }
    skeleton.hidden = false;
    if (pendingDestination) markRouteEntry(pendingDestination);
    window.requestAnimationFrame(() => skeleton.setAttribute('data-visible', ''));

    window.clearTimeout(recoveryTimer);
    recoveryTimer = window.setTimeout(() => {
      clearRouteEntry();
      hide();
    }, NAVIGATION_RECOVERY_DELAY);
  };

  const schedule = (variant = 'content') => {
    window.clearTimeout(showTimer);
    pendingVariant = variant;
    const destination = pendingDestination;
    clearRouteEntry();

    if (variant === 'home' || variant === 'careers' || variant === 'none') {
      if (destination && variant !== 'none') markRouteEntry(destination);
      hide();
      return;
    }

    if (variant === 'library-subscription') {
      if (destination) markRouteEntry(destination);
      hide();
      return;
    }

    setVariant(variant);
    if (destination) markRouteEntry(destination);
    showTimer = window.setTimeout(show, SHOW_DELAY);
  };

  const markRouteEntry = (destination) => {
    const variant = pendingVariant || pageLoadingVariant(destination);

    if (variant === 'none' || isAccountVariant(variant)) {
      try {
        window.sessionStorage.removeItem(ROUTE_ENTRY_STORAGE_KEY);
      } catch {}
      return;
    }

    try {
      window.sessionStorage.setItem(
        ROUTE_ENTRY_STORAGE_KEY,
        JSON.stringify({
          path: `${destination.pathname}${destination.search}`,
          variant,
          createdAt: Date.now(),
          shown: !skeleton.hidden,
          productState: pendingProductState,
        }),
      );
    } catch {}
  };

  const showBeforeNavigation = (destination, variant = pageLoadingVariant(destination)) => {
    pendingDestination = destination;
    schedule(variant);

    // Start the document request immediately; feedback must not delay navigation.
    window.location.assign(destination.href);
  };

  const pageLoadingVariant = (destination) => {
    const pathname = destination.pathname.replace(/\/+$/, '') || '/';
    const routePath = pathname.replace(/^\/[a-z]{2}(?:-[a-z]{2})?(?=\/|$)/i, '') || '/';

    if (/^\/(?:logout|(?:account|customer_identity|customer_authentication|authentication)\/logout)(?:\/|$)/.test(routePath)) return 'account-sign-out';
    if (/^\/account(?:\/login)?\/?$/.test(routePath)) return 'account-sign-in';
    if (/^\/(?:login|customer_authentication|authentication)(?:\/|$)/.test(routePath)) return 'account-sign-in';

    if (routePath === '/') {
      return 'home';
    }

    if (routePath === '/pages/coffee-finder' || (routePath === '/search' && destination.searchParams.get('view') === 'coffee-finder')) return 'coffee-finder';

    // The account shell is server-rendered; only its provider balance needs a skeleton.
    if (routePath === '/search' && destination.searchParams.get('view') === 'store-credit') return 'none';

    if (routePath === '/pages/careers' || routePath === '/pages/work-with-us') {
      return 'careers';
    }

    if (/^\/pages\/(?:rewards|rewards-hub|loyalty)$/.test(routePath)) {
      return 'loyalty';
    }

    if (routePath === '/collections/gemilai') {
      return 'gemilai';
    }

    if (routePath === '/pages/equipment-tools') {
      return 'equipment';
    }

    if (routePath === '/pages/roasters') {
      return 'roasters';
    }

    if (routePath === '/pages/contact') {
      return destination.searchParams.get('view') === 'warranty-repair' ? 'warranty' : 'contact';
    }

    const forYouSearchViews = ['for-you', 'for-you-account'];
    const isForYouRoute =
      routePath === '/pages/for-you' ||
      (routePath === '/search' && forYouSearchViews.includes(destination.searchParams.get('view')));

    if (routePath === '/search' && destination.searchParams.get('view') === 'for-you-signed-out') {
      return document.documentElement.hasAttribute('data-for-you-enabled') ? 'for-you-signed-out' : 'none';
    }

    if (isForYouRoute) {
      return document.documentElement.hasAttribute('data-for-you-enabled') ? 'for-you' : 'none';
    }

    if (routePath === '/products/the-library-subscription') {
      return 'library-subscription';
    }

    if (/^\/policies\/(shipping-policy|terms-of-service|refund-policy|privacy-policy|subscription-policy)$/.test(routePath)) {
      return 'none';
    }

    if (/^\/pages\/(giveaway|certifications)$/.test(routePath)) {
      return 'none';
    }

    if (/(^|\/)collections$/.test(routePath)) {
      return 'index';
    }

    if (/(^|\/)collections\//.test(routePath)) {
      return 'browse';
    }

    if (/(^|\/)products(?:\/|$)/.test(routePath)) {
      return 'product';
    }

    if (/(^|\/)search$/.test(routePath)) {
      if (!destination.searchParams.get('q')?.trim()) return 'search-recovery';
      return 'search';
    }

    if (/(^|\/)cart$/.test(routePath)) {
      return 'cart';
    }

    if (/(^|\/)blogs(?:\/|$)/.test(routePath)) {
      // Editorial text is server-rendered; image placeholders use the real media boxes.
      // A guessed route skeleton cannot match tags, saved list view or optional heroes.
      return 'none';
    }

    if (/(^|\/)pages\/(?:reviews-and-guides|ssp-burr-set-guide-2024|1zpresso-range-how-to-choose-your-grinder|aboutsculptor078|1zpresso-j-ultra-review|cleaning-your-grinder-disassembly-and-reassembly-of-the-j-max|sculptor-078-disassembly|blades-vs-burrs|1zpressoguide)$/.test(routePath)) return 'none';

    if (/(^|\/)(pages|policies)(?:\/|$)/.test(routePath)) {
      return 'content';
    }

    return 'content';
  };

  const accountNavigationVariant = (destination, link = null) => {
    const pathname = destination.pathname.replace(/\/+$/, '') || '/';
    const routePath = pathname.replace(/^\/[a-z]{2}(?:-[a-z]{2})?(?=\/|$)/i, '') || '/';
    const signOutPath = /^\/(?:logout|(?:account|customer_identity|customer_authentication|authentication)\/logout)(?:\/|$)/.test(routePath);
    const signInPath = /^\/account(?:\/login)?\/?$/.test(routePath) ||
      /^\/(?:login|customer_authentication|authentication)(?:\/|$)/.test(routePath);

    if (!signInPath && !signOutPath) return null;

    const storefrontHost = window.location.hostname.replace(/^www\./i, '');
    const trustedAccountHost = destination.hostname === `account.${storefrontHost}` || destination.hostname === 'account.sigmacoffee.co.uk';

    if (
      destination.origin !== window.location.origin &&
      (destination.protocol !== 'https:' || !trustedAccountHost)
    ) return null;

    const signOutLink = link?.matches('.site-header__profile-sign-out, .mobile-menu__profile-sign-out') ||
      link?.textContent?.trim().toLowerCase() === 'sign out';

    return signOutLink || signOutPath
      ? 'account-sign-out'
      : 'account-sign-in';
  };

  const pageNavigationDestination = (link) => {
    if (
      link.hasAttribute('download') ||
      (link.target && link.target.toLowerCase() !== '_self') ||
      link.hasAttribute('data-page-loading-ignore')
    ) {
      return null;
    }

    const destination = new URL(link.href, window.location.href);

    if (
      !['http:', 'https:'].includes(destination.protocol) ||
      (destination.origin !== window.location.origin && !accountNavigationVariant(destination, link))
    ) {
      return null;
    }

    if (
      destination.pathname === window.location.pathname &&
      destination.search === window.location.search &&
      destination.hash
    ) {
      return null;
    }

    return destination;
  };

  document.addEventListener('click', (event) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    const destination = link ? pageNavigationDestination(link) : null;

    if (destination) {
      const baseVariant = accountNavigationVariant(destination, link) || pageLoadingVariant(destination);
      const context = link.closest('[data-page-loading-route]');
      const hint = context?.dataset?.pageLoadingRoute;
      const variant = baseVariant === 'browse' && hint === 'browse-equipment'
        ? hint
        : baseVariant === 'product' && (hint === 'product-coffee' || hint === 'product-equipment')
          ? hint
          : baseVariant;
      pendingProductState = baseVariant === 'product' && context?.dataset?.productAvailability === 'sold-out'
        ? 'sold-out'
        : null;
      if (link.hasAttribute('data-page-loading-priority')) {
        event.preventDefault();
        showBeforeNavigation(destination, variant);
        return;
      }

      window.queueMicrotask(() => {
        if (!event.defaultPrevented) {
          pendingDestination = destination;
          schedule(variant);
        }
      });
    } else if (event.target instanceof Element && event.target.closest('#MainContent button, #MainContent input, #MainContent select')) {
      queueLayoutCapture();
    }
  });

  document.addEventListener('submit', (event) => {
    window.queueMicrotask(() => {
      const form = event.target;
      const submitter = event.submitter instanceof HTMLElement ? event.submitter : null;
      const target = submitter?.hasAttribute('formtarget')
        ? submitter.getAttribute('formtarget') || ''
        : form?.target || '';
      const method = (
        submitter?.formMethod ||
        form?.method ||
        'get'
      ).toLowerCase();

      if (
        event.defaultPrevented ||
        !(form instanceof HTMLFormElement) ||
        (target && target.toLowerCase() !== '_self') ||
        method === 'dialog' ||
        form.hasAttribute('data-page-loading-ignore')
      ) {
        return;
      }

      const action = submitter?.hasAttribute('formaction') ? submitter.formAction : form.action;
      const destination = new URL(action || window.location.href, window.location.href);

      if (
        !['http:', 'https:'].includes(destination.protocol) ||
        destination.origin !== window.location.origin
      ) {
        return;
      }

      if (method === 'get') {
        addGetParameters();
      }

      function addGetParameters() {
        let formData;

        try {
          formData = new FormData(form, submitter || undefined);
        } catch {
          formData = new FormData(form);
          const submitterName = submitter?.getAttribute('name');

          if (submitterName) {
            formData.append(submitterName, submitter.getAttribute('value') || '');
          }
        }

        destination.search = new URLSearchParams(formData).toString();
      }

      pendingDestination = destination;
      pendingProductState = null;
      schedule(pageLoadingVariant(destination));
    });
  });

  document.addEventListener(PAGE_LOADING_EVENT, (event) => {
    pendingDestination = null;
    pendingProductState = null;
    schedule(event.detail?.variant === 'browse' ? 'browse' : 'content');
  });

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      pendingDestination = null;
      pendingVariant = null;
      pendingProductState = null;
      clearRouteEntry();
      hide();
      return;
    }

    if (!document.documentElement.hasAttribute('data-page-loading-arrival')) {
      hide();
    }
  });

  window.addEventListener('pagehide', () => {
    window.clearTimeout(showTimer);
    window.clearTimeout(recoveryTimer);
    window.clearTimeout(captureTimer);
    window.cancelIdleCallback?.(captureIdle);
  });

  if (document.documentElement.hasAttribute('data-page-loading-arrival')) {
    const main = document.querySelector('#MainContent');
    const current = main?.querySelector('[data-page-loading-current-variant]');
    if (current) setVariant(current.dataset.pageLoadingCurrentVariant);
    if (!collectionIsPending()) {
      void loadLayoutApi().then((api) => {
        if (!api || skeleton.hidden || !main || collectionIsPending()) return;
        const layout = api.captureLoadingLayout(main);
        if (layout) paintLayout(layout);
      });
    }
  }

  document.fonts?.ready.then(queueLayoutCapture);
  window.addEventListener('load', queueLayoutCapture, { once: true });
  window.addEventListener('resize', () => {
    if (!skeleton.hidden) {
      syncHeaderOffset();
      layoutApi?.clearLoadingLayout(skeleton);
      const cached = pendingDestination && !isAccountVariant(pendingVariant) &&
        readLayouts().find((entry) => entry?.key === layoutKey(pendingDestination));
      if (cached?.layout) paintLayout(cached.layout);
    }
    queueLayoutCapture();
  }, { passive: true });
  document.addEventListener('sigma:collection-view:updated', queueLayoutCapture);
  const providers = typeof document.querySelectorAll === 'function' && typeof MutationObserver === 'function'
    ? document.querySelectorAll('[data-loyalty-loading-region], [data-for-you-loading]')
    : [];
  providers.forEach((region) => {
    const observer = new MutationObserver(() => {
      const ready = region.hidden || region.dataset.loyaltyLoadingState === 'ready';
      if (!ready) return;
      observer.disconnect();
      queueLayoutCapture();
    });
    observer.observe(region, { attributes: true, attributeFilter: ['hidden', 'data-loyalty-loading-state'] });
  });
}
